import { Lock, Sparkles, ShieldAlert, Eye, TrendingUp, MessageSquareHeart, Calendar, PartyPopper, ChevronRight } from "lucide-react";

/**
 * Free-tier pro teasers. Each component shows a partial preview to create
 * curiosity + a clear "one tap to unlock" trigger into the ReserveDealModal.
 *
 * Design contract: all gating is client-side UI only — the backend already
 * returned the full analysis. PRO users never render these (AnalyzePage
 * skips them when `hasFullAccess === true`).
 */

// ───── Simple client-side heuristics for free-tier previews ─────

// Detects shallow "red flag" patterns without needing an AI round-trip.
// Returns a count — the ACTUAL flag types are shown only to PRO.
export function detectRedFlags(text = "") {
  if (!text) return 0;
  const t = text.toLowerCase();
  let flags = 0;
  // Absolutes — "always / never"
  if (/\b(always|never)\b/.test(t)) flags++;
  // You-statements / accusations
  if (/\byou (always|never|don'?t|didn'?t|are|should|can'?t)\b/.test(t)) flags++;
  // Dismissive language
  if (/\b(whatever|fine then|forget it|nevermind)\b/.test(t)) flags++;
  // Emotional escalation markers
  if ((text.match(/!/g) || []).length >= 2) flags++;
  if (/\b(done|over it|sick of)\b/.test(t)) flags++;
  // Aggressive caps (>5 consecutive uppercase letters)
  if (/[A-Z]{5,}/.test(text)) flags++;
  return Math.min(flags, 5);
}

// Lightweight "Impact forecast" one-liner derived from risk_level.
// Designed to be coachy but not alarmist on green, direct on red.
export function shortImpactLine(riskLevel) {
  switch (riskLevel) {
    case "green":
      return { emoji: "😊", line: "They'll likely feel heard and respected." };
    case "yellow":
      return { emoji: "😕", line: "They may feel on edge and reread this twice." };
    case "red":
    default:
      return { emoji: "😤", line: "A defensive or shut-down reply is likely." };
  }
}

// ───── Components ─────

export function LockedRewriteCards({ count = 2, onUnlock }) {
  return (
    <div className="space-y-3" data-testid="locked-rewrite-cards">
      {Array.from({ length: count }).map((_, i) => (
        <button
          key={i}
          type="button"
          onClick={onUnlock}
          data-testid={`locked-rewrite-${i}`}
          className="w-full text-left relative overflow-hidden rounded-2xl border border-dashed border-[#4FB3A5]/40 bg-gradient-to-br from-[#F7F9FB] to-white p-4 active:scale-[0.99] transition-transform group"
        >
          {/* Blurred fake-content background */}
          <div
            className="absolute inset-0 px-4 py-3 text-[14px] leading-relaxed text-[#1F3A5F]/70 select-none pointer-events-none"
            style={{ filter: "blur(5px)" }}
            aria-hidden="true"
          >
            {i === 0
              ? "Hey — I've been thinking about this all day, and I want to say it without it turning into another fight. Here's what's actually on my mind…"
              : "I know it's late, but I don't want to go to bed still feeling like this. Can we talk tomorrow when we're both fresh?"}
          </div>

          {/* Foreground lock card */}
          <div className="relative flex items-center gap-3 py-5">
            <div className="w-10 h-10 rounded-xl bg-white border border-[#4FB3A5]/30 shadow-sm flex items-center justify-center flex-shrink-0">
              <Lock size={16} className="text-[#4FB3A5]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#4FB3A5]">
                PRO · Tone #{i + 2}
              </div>
              <div className="text-[13px] font-semibold text-[#1F3A5F]">
                {i === 0 ? "Empathetic rewrite" : "Firm-but-kind rewrite"}
              </div>
              <div className="text-[11px] text-[#6B7280] mt-0.5">
                Tap to unlock 2 more calm rewrites
              </div>
            </div>
            <div className="inline-flex items-center gap-1 bg-[#1F3A5F] text-white text-[10px] font-bold px-2.5 py-1 rounded-full group-hover:bg-[#2a4a75]">
              <Sparkles size={10} />
              Unlock
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

export function MoreIssuesChip({ hiddenCount, onUnlock }) {
  if (hiddenCount <= 0) return null;
  return (
    <button
      type="button"
      onClick={onUnlock}
      data-testid="more-issues-chip"
      className="inline-flex items-center gap-1.5 bg-white border border-dashed border-[#4FB3A5]/50 text-[#1F3A5F] text-[12px] font-semibold py-1.5 px-3 rounded-lg hover:bg-[#EAF6F4] transition-colors"
    >
      <Lock size={11} className="text-[#4FB3A5]" />
      +{hiddenCount} more detected
      <span className="text-[10px] uppercase tracking-wider text-[#4FB3A5] ml-0.5">PRO</span>
    </button>
  );
}

export function ImpactForecastCard({ riskLevel, onUnlock }) {
  const { emoji, line } = shortImpactLine(riskLevel);
  return (
    <div
      className="bg-white rounded-2xl shadow-soft p-5"
      data-testid="impact-forecast-card"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-[#EEF3FF] flex items-center justify-center">
          <Eye size={18} className="text-[#2F5BFF]" />
        </div>
        <div>
          <h3 className="font-semibold text-[15px] text-[#1F3A5F]">
            Impact forecast
          </h3>
          <p className="text-[12px] text-[#6B7280]">How the other person will read this</p>
        </div>
      </div>

      {/* Free preview — single emoji read */}
      <div className="bg-[#F7F9FB] rounded-xl p-4 mb-3 flex items-center gap-3" data-testid="impact-forecast-free">
        <span className="text-3xl leading-none">{emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#A0A7B4] mb-0.5">
            Reader's likely feeling
          </div>
          <div className="text-[14px] font-semibold text-[#1F3A5F] leading-snug">{line}</div>
        </div>
      </div>

      {/* PRO locked metrics */}
      <button
        type="button"
        onClick={onUnlock}
        data-testid="impact-forecast-unlock"
        className="w-full relative rounded-xl border border-dashed border-[#4FB3A5]/40 overflow-hidden active:scale-[0.99] transition-transform"
      >
        <div
          className="absolute inset-0 p-4 text-left pointer-events-none select-none"
          style={{ filter: "blur(5px)" }}
          aria-hidden="true"
        >
          <div className="grid grid-cols-3 gap-3">
            {["Emotional tone", "Reply likelihood", "Reply tone"].map((k, i) => (
              <div key={k}>
                <div className="text-[10px] font-bold text-[#6B7280]">{k}</div>
                <div className="h-2 bg-[#E5E7EB] rounded-full mt-1">
                  <div
                    className="h-2 rounded-full"
                    style={{
                      width: `${[68, 42, 81][i]}%`,
                      background: "linear-gradient(90deg, #4FB3A5, #2F5BFF)",
                    }}
                  />
                </div>
                <div className="text-[11px] font-semibold text-[#1F3A5F] mt-1">
                  {["Hurt · 68%", "4h delay", "Defensive"][i]}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative bg-white/70 backdrop-blur-[1px] p-4 flex items-center justify-center gap-2 min-h-[96px]">
          <Lock size={14} className="text-[#4FB3A5]" />
          <span className="text-[12px] font-semibold text-[#1F3A5F]">
            Unlock full forecast — emotional tone, reply likelihood & reply tone
          </span>
        </div>
      </button>
    </div>
  );
}

export function RecapsTeaserCard({ onOpenPreview, onUnlock }) {
  return (
    <div
      className="rounded-2xl p-5 text-white shadow-xl relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #1F3A5F 0%, #2a4a75 40%, #4FB3A5 100%)",
      }}
      data-testid="recaps-teaser-card"
    >
      <span
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 90% 15%, rgba(255,255,255,0.6), transparent 45%)",
        }}
      />
      <div className="relative">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#FFD27F]">
            <PartyPopper size={11} /> Coming at launch
          </span>
        </div>
        <h3 className="text-lg font-bold leading-tight mb-1">
          Your recaps are coming.
        </h3>
        <p className="text-[12px] text-white/85 leading-snug mb-3">
          Every analysis you run feeds two things: a monthly coaching recap and
          your year-end Texting Wrapped™.
        </p>

        {/* Thumbnails */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button
            type="button"
            onClick={onOpenPreview}
            data-testid="recaps-teaser-monthly"
            className="relative rounded-xl overflow-hidden bg-white text-left p-3 shadow-md active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center gap-1 mb-1">
              <Calendar size={11} className="text-[#4FB3A5]" />
              <span className="text-[9px] uppercase tracking-widest font-bold text-[#4FB3A5]">
                Monthly
              </span>
            </div>
            <div className="text-[18px] font-black text-[#1F3A5F] leading-none">64</div>
            <div className="text-[10px] text-[#6B7280] leading-snug">
              texts rewritten · June
            </div>
            <div className="mt-2 flex items-center gap-1 text-[10px] font-bold text-[#4FB3A5]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4FB3A5]" />
              +144% calm days
            </div>
          </button>
          <button
            type="button"
            onClick={onOpenPreview}
            data-testid="recaps-teaser-yearly"
            className="relative rounded-xl overflow-hidden text-left p-3 shadow-md active:scale-[0.98] transition-transform"
            style={{
              background: "linear-gradient(145deg, #0B1A33 0%, #1F3A5F 60%, #2F5BFF 110%)",
            }}
          >
            <div className="flex items-center gap-1 mb-1">
              <Sparkles size={11} className="text-[#FFD27F]" />
              <span className="text-[9px] uppercase tracking-widest font-bold text-[#FFD27F]">
                Yearly Wrapped™
              </span>
            </div>
            <div className="text-[18px] font-black text-white leading-none">847</div>
            <div className="text-[10px] text-white/75 leading-snug">
              texts saved this year
            </div>
            <div className="mt-2 flex items-center gap-1 text-[10px] font-bold text-white/90">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FFD27F]" />
              Shareable IG card
            </div>
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onOpenPreview}
            data-testid="recaps-teaser-preview-btn"
            className="flex-1 bg-white/15 backdrop-blur-sm border border-white/25 text-white font-semibold text-[12px] py-2.5 rounded-xl hover:bg-white/20 active:scale-95 transition-all inline-flex items-center justify-center gap-1.5"
          >
            See full demo <ChevronRight size={12} />
          </button>
          <button
            type="button"
            onClick={onUnlock}
            data-testid="recaps-teaser-reserve-btn"
            className="flex-1 bg-white text-[#1F3A5F] font-black text-[12px] py-2.5 rounded-xl hover:bg-white/90 active:scale-95 transition-all shadow-md"
          >
            Reserve $5.99/yr
          </button>
        </div>
      </div>
    </div>
  );
}


export function RedFlagPreviewCard({ originalText, onUnlock }) {
  const count = detectRedFlags(originalText);
  if (count === 0) return null;
  return (
    <button
      type="button"
      onClick={onUnlock}
      data-testid="red-flag-preview-card"
      className="w-full text-left bg-white rounded-2xl shadow-soft p-5 border border-[#FEE4E4] active:scale-[0.99] transition-transform"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FEF3F2] flex items-center justify-center flex-shrink-0">
          <ShieldAlert size={18} className="text-[#E07A7A]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="font-semibold text-[15px] text-[#1F3A5F]">
              Red-flag detector
            </h3>
            <span className="inline-flex items-center gap-1 bg-[#FEE4E4] text-[#E07A7A] text-[10px] font-bold px-2 py-0.5 rounded-full">
              {count} detected
            </span>
          </div>
          <p className="text-[12px] text-[#6B7280] leading-snug mb-2">
            We spotted {count} phrasing pattern{count === 1 ? "" : "s"} that often
            escalate conflict. Tap to see which and how to swap them.
          </p>
          <div className="inline-flex items-center gap-1 text-[11px] font-bold text-[#4FB3A5]">
            <Lock size={11} />
            Unlock types (PRO)
          </div>
        </div>
      </div>
    </button>
  );
}
